import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { DatetimeString } from '#/types/TypedDateString';

const TRANSLATIONS = {
  [I18nLocale.EN]: {
    lessThan1m: 'less than a minute',
    minute: (n) => (n === 1 ? '1 minute' : `${n} minutes`),
    hour: (n) => (n === 1 ? '1 hour' : `${n} hours`),
    day: (n) => (n === 1 ? '1 day' : `${n} days`),
    month: (n) => (n === 1 ? '1 month' : `${n} months`),
    year: (n) => (n === 1 ? '1 year' : `${n} years`),
    past: (s: string) => `${s} ago`,
    future: (s: string) => `in ${s}`,
  },

  [I18nLocale.ES]: {
    lessThan1m: 'menos de un minuto',
    minute: (n) => (n === 1 ? '1 minuto' : `${n} minutos`),
    hour: (n) => (n === 1 ? '1 hora' : `${n} horas`),
    day: (n) => (n === 1 ? '1 día' : `${n} días`),
    month: (n) => (n === 1 ? '1 mes' : `${n} meses`),
    year: (n) => (n === 1 ? '1 año' : `${n} años`),
    past: (s: string) => `hace ${s}`,
    future: (s: string) => `en ${s}`,
  },

  [I18nLocale.PT]: {
    lessThan1m: 'menos de um minuto',
    minute: (n) => (n === 1 ? '1 minuto' : `${n} minutos`),
    hour: (n) => (n === 1 ? '1 hora' : `${n} horas`),
    day: (n) => (n === 1 ? '1 dia' : `${n} dias`),
    month: (n) => (n === 1 ? '1 mês' : `${n} meses`),
    year: (n) => (n === 1 ? '1 ano' : `${n} anos`),
    past: (s) => `há ${s}`,
    future: (s) => `em ${s}`,
  },
} satisfies Record<
  I18nLocale,
  {
    lessThan1m: string;
    minute: (n: number) => string;
    hour: (n: number) => string;
    day: (n: number) => string;
    month: (n: number) => string;
    year: (n: number) => string;
    past: (s: string) => string;
    future: (s: string) => string;
  }
>;

export function timeago(
  date: DatetimeString,
  { locale, now }: { locale: I18nLocale; now: DatetimeString },
) {
  const diffMs = new Date(date).getTime() - new Date(now).getTime();
  const t = TRANSLATIONS[locale];

  const str = (() => {
    const seconds = Math.round(Math.abs(diffMs) / 1000);
    if (seconds < 60) return t.lessThan1m;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t.minute(minutes);

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t.hour(hours);

    const days = Math.floor(hours / 24);
    if (days < 30) return t.day(days);

    const months = Math.floor(days / 30);
    if (months < 12) return t.month(months);

    const years = Math.floor(days / 365);
    return t.year(years);
  })();

  return diffMs > 0 ? t.future(str) : t.past(str);
}
